<script setup>
import { useRoute } from 'vue-router'
const $route = useRoute()

import navItems from '@/navigation/horizontal'
import { themeConfig } from '@themeConfig'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import restaurantAdminNavItems from '@/navigation/vertical/restaurantAdmin'

// Composable
import { useSkins } from '@core/composable/useSkins'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import { HorizontalNavLayout } from '@layouts'

// Components
import Footer from '@/layouts/components/Footer.vue'
import NavBarNotifications from '@/layouts/components/NavBarNotifications.vue'
import UserMenu from '@/layouts/components/UserMenu.vue'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import ModeSwitcherDrawer from '@core/components/ModeSwitcherDrawer.vue'
import { useReservationsStore, useUserGroupsStore, useUsersStore, useAppModeStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { canUseRestaurantAdminMode } from '@/utils/appModeEligibility'
import {storeToRefs} from 'pinia'

const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const appModeStore = useAppModeStore()
const { mode: appMode, isDrawerOpen: isModeSwitcherOpen } = storeToRefs(appModeStore)

const isRestaurantAdminEligible = computed(() => canUseRestaurantAdminMode(myUser.value))

// Desktop admin nav links: Bookings + Fans only (Discover lives on the mobile tabs,
// Profile lives on the avatar link). Sourced from the shared nav config.
const desktopAdminNavItems = computed(() =>
  restaurantAdminNavItems.filter(item => item.to === '/admin/bookings' || item.to === '/admin/fans'),
)


const { appRouteTransition } = useThemeConfig()
const { layoutAttrs, injectSkinClasses } = useSkins()

const userGroupsStore = useUserGroupsStore()
const { guestUserGroup } = storeToRefs(userGroupsStore)


let showingMenu = ref(false)

const avatarBadgeProps = {
  dot: true,
  modelValue: false,
  location: 'bottom right',
  offsetX: 3,
  offsetY: 3,
  color: 'success',
  bordered: true
}

// ℹ️ This will inject classes in body tag for accurate styling
injectSkinClasses()

const homeIconPath = computed(() => {
  if (guestUserGroup.value?.joinUrl) {
    return guestUserGroup.value.joinUrl
  }
  if (myUser?.value && myUser?.value?.userLevel != 'dummy') {
    return '/discover'
  }

  return '/home/'
})

const backHome = computed(() => {
  if (myUser?.value && myUser?.value?.userLevel != 'dummy') {
    return '/discover'
  }
  
  return '/home/'
})

// Dynamic title state that pages can update
const dynamicPageTitle = ref('') // Default blank title

const getTitle = path => {
  if (path.includes('restaurant-bookings')) {
    return { title: dynamicPageTitle.value }
  }
  if (path.includes('restaurant-favorites')) {
    return { title: `Preferred` }
  }
  if (path.includes('restaurant-spaces')) {
    return { title: `Group/Private Dining Spaces` }
  }
  if (path.includes('restaurant-happenings')) {
    return { title: `Happenings` }
  }
  
  return
}

// Expose the dynamic title for pages to update
provide('dynamicPageTitle', dynamicPageTitle)

const newDraft = async () => {
  if (!myUser.value) {
    await usersStore.loginNewDummyUser()
  }
  await usersStore.startNewReservation()
}
</script>

<template>
  <div>
    <ModeSwitcherDrawer v-model="isModeSwitcherOpen" />
    <HorizontalNavLayout
      :nav-items="navItems"
      v-bind="layoutAttrs"
    >
      <!-- 👉 navbar -->
      <template #navbar>
        <!-- Mobile Search Bar (shown on all mobile pages) -->
        <div
          v-if="$vuetify.display.smAndDown && !getTitle($route.path)"
          class="mobile-search-container"
        >
          <div class="mobile-search-bar">
            <div class="mobile-search-input-wrapper">
              <MainSearch
                :display-on-masthead="true"
                :mobile-mode="true"
              />
            </div>
            <NavBarNotifications v-if="myUser && myUser.userLevel != 'dummy'" />
          </div>
        </div>

        <div
          v-if="getTitle($route.path) && $vuetify.display.smAndDown"
          class="navbar"
        >
          <VRow class="navbar-main d-flex justify-center pa-0">
            <VCol
              cols="auto"
              class="d-flex align-center"
            >
              <a
                :href="backHome"
                class="d-flex align-center"
              >
                <VIcon color="secondary">mdi-chevron-left</VIcon>
              </a>
            </VCol>
            <VSpacer />
            <VCol
              cols="auto"
              class="d-flex align-center justify-center"
            >
              <h1 class="text-center text-subtitle-1">
                {{ getTitle($route.path).title }}
              </h1>
            </VCol>
            <VSpacer />
            <VCol
              cols="auto"
              class="d-flex justify-end pb-2"
            >
              <div
                class="navbar-mobile-overlay"
                @click="showingMenu = false"
              />
            </VCol>
          </VRow>
          <VRow class="navbar-main pa-0 mt-2">
            <VCol
              cols="12"
              class="d-flex align-center pb-2 px-3"
            >
              <MainSearch :display-on-masthead="true" />
            </VCol>
          </VRow>
        </div>
        <!-- Desktop Navbar (hide on mobile when mobile search is shown) -->
        <div
          v-else-if="$vuetify.display.mdAndUp"
          class="navbar"
        >
          <VRow class="navbar-main pa-0">
            <VCol
              cols="auto"
              class="d-flex align-center ps-0 pe-0 pb-2 navbar-logo"
            >
              <a
                :href="homeIconPath"
                class="d-flex align-center mr-2"
                @click="showingMenu = false;"
              >
                <VNodeRenderer :nodes="themeConfig.app.logo" />
              </a>
              <div
                class="navbar-mobile-overlay"
                @click="showingMenu = false"
              />
            </VCol>
            <VCol class="d-flex align-center pb-2 navbar-search">
              <div
                class="navbar-main-links"
                style="width: 100%;"
              >
                <MainSearch :display-on-masthead="true" />
              </div>
            </VCol>
            <VCol
              cols="auto"
              class="d-flex justify-end align-center pl-0 pr-0 pb-2 navbar-profile"
            >
              <div
                class="navbar-mobile-overlay"
                @click="showingMenu = false"
              />


              <div
                v-if="myUser && myUser.userLevel != 'dummy'"
                class="desktop-nav-link desktop-nav-notifications"
              >
                <NavBarNotifications />
                <span>Notifications</span>
              </div>
              <!--
                <VBtn
                v-if="!myUser || myUser.userLevel == 'dummy'"
                class="cursor-pointer"
                :size="$vuetify.display.smAndDown ? 'x-small' : 'x-small'"
                color="primary"
                icon="mdi-plus"
                width="23"
                height="23"
                @click="newDraft"
                >
                </VBtn> 
              -->

              <!-- Member mode nav links (hidden in Restaurant Admin mode) -->
              <template v-if="appMode !== 'restaurantAdmin'">
                <!-- Bookings and Favorites Links (Desktop Only) -->
                <RouterLink
                  v-if="$vuetify.display.mdAndUp"
                  to="/bookings"
                  class="desktop-nav-link"
                  aria-label="Bookings"
                >
                  <VIcon size="22">
                    mdi-calendar
                  </VIcon>
                  <span>{{ $t('nav.bookings') }}</span>
                </RouterLink>

                <RouterLink
                  v-if="$vuetify.display.mdAndUp"
                  to="/ai"
                  class="desktop-nav-link"
                  aria-label="RM AI"
                >
                  <VIcon size="22">
                    mdi-square-edit-outline
                  </VIcon>
                  <span>RM AI</span>
                </RouterLink>

                <RouterLink
                  v-if="$vuetify.display.mdAndUp"
                  to="/favorites"
                  class="desktop-nav-link"
                  aria-label="Favorites"
                >
                  <VIcon size="22">
                    mdi-heart-outline
                  </VIcon>
                  <span>{{ $t('nav.favorites') }}</span>
                </RouterLink>
              </template>

              <!-- Restaurant Admin mode nav links (Desktop Only) — Bookings, [Admin AI], Fans; Discover + Profile live in their own slots -->
              <template v-else-if="$vuetify.display.mdAndUp">
                <RouterLink
                  v-for="item in desktopAdminNavItems.slice(0, 1)"
                  :key="item.to"
                  :to="item.to"
                  class="desktop-nav-link"
                  :aria-label="item.title"
                >
                  <VIcon size="22">
                    {{ item.icon.icon }}
                  </VIcon>
                  <span>{{ item.title }}</span>
                </RouterLink>

                <!-- Admin Agent (platform admins only) — centered like the member-mode RM AI link -->
                <RouterLink
                  v-if="myUser?.userLevel === 'admin'"
                  to="/ai/admin"
                  class="desktop-nav-link"
                  aria-label="RM Team"
                >
                  <VIcon size="22">
                    mdi-square-edit-outline
                  </VIcon>
                  <span>RM Team</span>
                </RouterLink>

                <RouterLink
                  v-for="item in desktopAdminNavItems.slice(1)"
                  :key="item.to"
                  :to="item.to"
                  class="desktop-nav-link"
                  :aria-label="item.title"
                >
                  <VIcon size="22">
                    {{ item.icon.icon }}
                  </VIcon>
                  <span>{{ item.title }}</span>
                </RouterLink>
              </template>


              <!-- <UserMenu v-if="(myUser && (myUser.userLevel == 'dummy')) || !myUser"/> -->
              <RouterLink
                v-if="!myUser || myUser.userLevel == 'dummy'"
                to="/login"
                class="desktop-nav-link"
              >
                <VBadge v-bind="avatarBadgeProps">
                  <VAvatar
                    class="cursor-pointer"
                    color="primary"
                    size="22"
                    variant="tonal"
                  >
                    <div class="avatar-fallback flex flex-row align-center justify-center">
                      <span class="avatar-fallback-text text-xl"><VIcon>mdi-user</VIcon></span>
                    </div>
                  </VAvatar>
                </VBadge>
                <span>{{ $t('nav.login') }}</span>
              </RouterLink>
              <RouterLink
                v-else-if="appMode === 'restaurantAdmin'"
                to="/admin/profile"
                class="desktop-nav-link"
                aria-label="Account"
              >
                <VIcon size="22">
                  mdi-storefront-outline
                </VIcon>
                <span>Account</span>
              </RouterLink>
              <RouterLink
                v-else
                to="/m"
                class="desktop-nav-link"
              >
                <VBadge v-bind="avatarBadgeProps">
                  <VAvatar
                    class="cursor-pointer"
                    color="primary"
                    size="22"
                    variant="tonal"
                  >
                    <VImg
                      v-if="myUser && myUser && myUser.profileImageId"
                      cover
                      :src="mediaUrl(myUser.profileImageId, 'thumb')"
                    />
                    <div
                      v-else-if="myUser?.firstName?.length > 0 || myUser?.lastName?.length > 0"
                      class="avatar-fallback flex flex-row align-center justify-center"
                    >
                      <span class="avatar-fallback-text">{{ myUser?.firstName?.[0]?.toUpperCase() }}{{ myUser?.lastName?.[0]?.toUpperCase() }}</span>
                    </div>
                    <div
                      v-else
                      class="avatar-fallback flex flex-row align-center justify-center"
                    >
                      <span class="avatar-fallback-text text-xl"><VIcon>mdi-user</VIcon></span>
                    </div>
                  </VAvatar>
                </VBadge>
                <span>{{ $t('nav.profile') }}</span>
              </RouterLink>
            </VCol>
          </VRow>
        </div>
      </template>

      <!-- 👉 Pages -->
      <RouterView v-slot="{ Component, route }">
        <Transition
          :name="appRouteTransition"
          mode="out-in"
        >
          <Component
            :is="Component"
            :key="route.path"
          />
        </Transition>
      </RouterView>

      <!-- 👉 Footer -->
      <template #footer>
        <Footer />
      </template>
    </HorizontalNavLayout>
  </div>
</template>

<style>
/* Global mobile bottom nav padding */
@media (max-width: 959px) {
  .layout-wrapper {
    padding-bottom: 60px;
  }
}
</style>

<style scoped>
.navbar-logo,
.navbar-profile {
  transition: opacity 0.3s ease, width 0.3s ease, max-width 0.3s ease;
}

.desktop-nav-link {
  position: relative; /* anchor for the absolutely-hung Beta tag */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 12px;
  margin: 0 4px;
  text-decoration: none;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: color 0.2s ease;
  cursor: pointer;
  background: transparent;
  border: none;
  font-family: inherit;
}

.desktop-nav-link:hover {
  color: rgb(var(--v-theme-primary));
}

.desktop-nav-link span {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.desktop-nav-link.router-link-active {
  color: rgb(var(--v-theme-primary));
}

.mobile-search-container {
  padding: 12px 16px;
  background-color: rgb(var(--v-theme-surface));
  width: 100%;
}

.mobile-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.mobile-search-bar :deep(#notification-btn) {
  flex-shrink: 0;
  min-width: unset !important;
  min-height: unset !important;
  padding: 0 !important;
  height: 32px !important;
  width: 32px !important;
  opacity: 1 !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
}

.mobile-search-bar :deep(#notification-btn .v-btn__overlay),
.mobile-search-bar :deep(#notification-btn .v-btn__underlay) {
  display: none !important;
}

.mobile-search-bar :deep(#notification-btn .v-icon) {
  font-size: 24px;
}

.mobile-search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 0;
}

.desktop-nav-notifications :deep(#notification-btn) {
  min-width: unset !important;
  min-height: unset !important;
  padding: 0 !important;
  height: 22px !important;
  width: 22px !important;
  line-height: 22px !important;
  opacity: 1 !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 0 0 22px !important;
  overflow: visible !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

.desktop-nav-notifications :deep(#notification-btn .v-btn__content) {
  height: 22px !important;
  width: 22px !important;
  line-height: 22px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.desktop-nav-notifications :deep(#notification-btn .v-btn__overlay),
.desktop-nav-notifications :deep(#notification-btn .v-btn__underlay) {
  display: none !important;
}

.desktop-nav-notifications :deep(#notification-btn .v-icon) {
  font-size: 22px;
  height: 22px;
  width: 22px;
  line-height: 22px;
}

.desktop-nav-notifications:hover :deep(#notification-btn) {
  color: rgb(var(--v-theme-primary)) !important;
}

.beta-tag {
  /* Hangs below the item without adding to its height, so the RM AI icon
     stays on the same line as the other nav icons */
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--ds-brand-primary-darker);
  line-height: 1;
  margin-top: -6px; /* tucked close under "RM AI" so it isn't clipped */
}

.admin-mode-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  background: var(--ds-brand-primary-dark, #3F998F);
  margin-right: 4px;
}

.admin-mode-badge span {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
}

.admin-mode-badge__icon {
  color: #fff !important;
}

.mode-switcher-btn {
  border-radius: 8px;
}

.mode-switcher-btn:hover {
  background: rgba(var(--v-theme-primary), 0.06);
}
</style>
